import React, { useEffect, useMemo, useState } from 'react';

import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import formatMoney from '@julofinance/web-helpers/dist/string/formatMoney';

import Page from 'components/Page';

import {
  useRVerifyPin,
  useRRegisterPrivy,
  useRGetAgreementSummary,
  useRGetPrivyRegistrationStatus,
  useRGetDigiSignFeatureSettings,
} from '@repositories';
import useGlobalState from 'actions';
import { useProfile } from '@hooks/use-profile';

import {
  actionButtonContainer,
  agreementContainer,
  bottomSheetContent,
  pageBackground,
} from './styles';
import { useActionLog } from '@hooks/use-action-log';
import NewSubHeader from 'components/NewSubHeader';
import { Card, Typography, julo, Button } from '@julo-ui/react';
import { ChevronRightOutlined } from '@mui/icons-material';
import BottomSheet from '@new-components/elements/bottom-sheet';
import { DocumentIcon } from '@new-components/shapes';
import { useSnackbar, InputPin } from '@new-components/elements';
import { SERVER_ERROR } from 'store/error/constants';

const AgreementSummaryPage = () => {
  const [globalState, { openLoadingOverlay, closeLoadingOverlay }] =
    useGlobalState();

  const history = useHistory();
  const [showInputPin, setShowInputPin] = useState(false);
  const [showPreviewDoc, setShowPreviewDoc] = useState(false);
  const [pinErrorMessage, setPinErrorMessage] = useState('');
  const [isPrivyRegistered, setIsPrivyRegistered] = useState(false);
  const [isActiveDigiSign, setIsActiveDigiSign] = useState(false);
  const { onOpen } = useSnackbar();

  useActionLog({ page: 'ringkasan_perjanjian_page' });

  const { data: profile, isLoading } = useProfile();
  const loanXid = profile?.loan?.loanXid;

  const redirectToAgreementLetter = () => {
    history.push(`/agreement_summary/${loanXid}/skrtp`);
  };

  useRGetDigiSignFeatureSettings({
    onSuccess: (res) => {
      setIsActiveDigiSign(res?.data?.isActive);
    },
  });

  const { mutate: registerOnPrivy } = useRRegisterPrivy({
    onSettled: () => {
      closeLoadingOverlay();
      redirectToAgreementLetter();
    },
  });

  useRGetPrivyRegistrationStatus(
    {
      enabled: !!profile?.application?.applicationId && isActiveDigiSign,
      onSuccess: (response) => {
        const status = response.data.status.toLowerCase();
        if (status === 'registered' || status === 'verified') {
          setIsPrivyRegistered(true);
        } else {
          setIsPrivyRegistered(false);
        }
      },
      onError: () => {
        setIsPrivyRegistered(false);
      },
    },
    Number(profile?.application?.applicationId),
  );

  const { data: agreementSummaryResponse } = useRGetAgreementSummary({
    loanXid,
    enabled: !!loanXid,
  });

  const agreementSummaryData = useMemo(
    () => agreementSummaryResponse?.data || { loan: {}, bank: {} },
    [agreementSummaryResponse?.data],
  );

  const loanSummary = useMemo(
    () => agreementSummaryData.loan || {},
    [agreementSummaryData],
  );

  const bankSummary = useMemo(
    () => agreementSummaryData.bank || {},
    [agreementSummaryData],
  );

  const { mutate: verifyPin } = useRVerifyPin({
    onSuccess: () => {
      if (isActiveDigiSign) {
        registerOnPrivy({
          variables: {
            application_id: Number(profile?.application?.applicationId || 0),
          },
        });
      }

      redirectToAgreementLetter();
      closeLoadingOverlay();
    },
    onError: (err) => {
      const errors = err.payload?.errors as Array<string>;
      const errorMessage = errors?.[0];

      if (errorMessage) {
        setPinErrorMessage(errorMessage);
      } else {
        onOpen(SERVER_ERROR);
      }

      closeLoadingOverlay();
    },
  });

  const handleVerifyPin = (pinValue: string) => {
    openLoadingOverlay();
    setPinErrorMessage('');

    const payload = {
      pin: pinValue,
      application_id: Number(profile?.application?.applicationId || 0),
      purpose: 'digisign',
    };

    verifyPin({ variables: payload });
  };

  useEffect(() => {
    if (!loanXid && !isLoading) {
      onOpen({
        title: globalState.noLoanXidError,
        severity: 'error',
      });
    }
  }, [globalState.noLoanXidError, isLoading, loanXid, onOpen]);

  useEffect(() => {
    setPinErrorMessage('');
  }, [showInputPin]);

  if (showInputPin) {
    return (
      <InputPin
        subHeaderTitle='Verifikasi'
        errorMessage={{ title: pinErrorMessage }}
        onSubmit={handleVerifyPin}
        onClose={() => setShowInputPin(false)}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>JULO - Ringkasan Perjanjian</title>
      </Helmet>
      <Page useHeader={true} className={pageBackground}>
        <NewSubHeader
          title='Ringkasan Perjanjian'
          onClickBack={() => history.push('/home')}
        />
        <julo.div className={agreementContainer}>
          <Card className='grid'>
            <Typography type='body' color='var(--colors-neutrals-80)'>
              Jumlah pinjaman
            </Typography>
            <Typography type='body' color='var(--colors-neutrals-90)' bold>
              {formatMoney(loanSummary.amount || 0)}
            </Typography>

            <Typography type='body' color='var(--colors-neutrals-80)'>
              Jangka waktu
            </Typography>
            <Typography type='body' color='var(--colors-neutrals-90)' bold>
              {loanSummary.tenure} hari
            </Typography>

            <Typography type='body' color='var(--colors-neutrals-80)'>
              Bunga per bulan
            </Typography>
            <Typography type='body' color='var(--colors-neutrals-90)' bold>
              {loanSummary.interestRate}%
            </Typography>

            <Typography type='body' color='var(--colors-neutrals-80)'>
              Dana cair
            </Typography>
            <Typography type='body' color='var(--colors-neutrals-90)' bold>
              {formatMoney(loanSummary.disbursalAmount || 0)}
            </Typography>

            <Typography type='body' color='var(--colors-neutrals-80)'>
              Cicilan per hari
            </Typography>
            <Typography type='body' color='var(--colors-neutrals-90)' bold>
              {formatMoney(loanSummary.dailyRepayment || 0)}
            </Typography>

            <Typography type='body' color='var(--colors-neutrals-80)'>
              Biaya admin
            </Typography>
            <Typography type='body' color='var(--colors-neutrals-90)' bold>
              {formatMoney(loanSummary.upfrontFee || 0)}
            </Typography>

            {(loanSummary?.additionalFee || 0) > 0 && (
              <>
                <Typography type='body' color='var(--colors-neutrals-80)'>
                  Biaya tambahan
                </Typography>
                <Typography type='body' color='var(--colors-neutrals-90)' bold>
                  {formatMoney(loanSummary.additionalFee || 0)}
                </Typography>
              </>
            )}
          </Card>

          <Typography type='body' bold sx={{ margin: '16px 0' }}>
            Informasi Rekening Penerima
          </Typography>

          <Card className='flex'>
            <Typography type='body' color='var(--colors-neutrals-80)'>
              Nama bank
            </Typography>
            <Typography type='body' color='var(--colors-neutrals-90)' bold>
              {bankSummary.bankName}
            </Typography>

            <Typography type='body' color='var(--colors-neutrals-80)'>
              Atas nama
            </Typography>
            <Typography type='body' color='var(--colors-neutrals-90)' bold>
              {bankSummary.accountName}
            </Typography>

            <Typography type='body' color='var(--colors-neutrals-80)'>
              Nomor rekening
            </Typography>
            <Typography type='body' color='var(--colors-neutrals-90)' bold>
              {bankSummary.accountNumber}
            </Typography>
          </Card>
          <julo.div className='loan-agreement'>
            <Typography
              type='body'
              color='var(--colors-primary-30)'
              bold
              onClick={() => setShowPreviewDoc(true)}
            >
              Lihat SKRTP dan RIPLAY
              <ChevronRightOutlined htmlColor='var(--colors-primary-30)' />
            </Typography>
          </julo.div>

          <BottomSheet
            title='Lihat Dokumen'
            display={showPreviewDoc}
            onClose={() => setShowPreviewDoc(false)}
            noAction
          >
            <julo.div className={bottomSheetContent}>
              <julo.div
                className='doc-card'
                onClick={() =>
                  history.push(`/agreement_letter/${loanXid}/skrtp`)
                }
              >
                <DocumentIcon />
                <julo.div>
                  <Typography type='body' bold size='small'>
                    SKRTP
                  </Typography>
                  <Typography type='caption' color='var(--colors-neutrals-70)'>
                    Surat Konfirmasi Rincian Transaksi Pendanaan
                  </Typography>
                </julo.div>
              </julo.div>

              <julo.div
                className='doc-card'
                onClick={() =>
                  history.push(`/agreement_letter/${loanXid}/riplay`)
                }
              >
                <DocumentIcon />
                <julo.div>
                  <Typography type='body' bold size='small'>
                    RIPLAY
                  </Typography>
                  <Typography type='caption' color='var(--colors-neutrals-70)'>
                    Ringkasan Informasi Produk dan Layanan
                  </Typography>
                </julo.div>
              </julo.div>
            </julo.div>
          </BottomSheet>
        </julo.div>

        <julo.div className={actionButtonContainer}>
          {!isPrivyRegistered && isActiveDigiSign && (
            <Typography
              type='body'
              fontSize='.8rem'
              color='var(--colors-neutrals-70)'
            >
              Dengan lanjut, kamu setuju untuk
              <julo.u
                sx={{ color: 'var(--colors-primary-30)', cursor: 'pointer' }}
                onClick={() => history.push('/privy-registration')}
              >
                daftar TTD Elektronik
              </julo.u>
            </Typography>
          )}
          <Button block onClick={() => setShowInputPin(true)}>
            Tanda Tangan Elektronik
          </Button>
        </julo.div>
      </Page>
    </>
  );
};

export default AgreementSummaryPage;
